import { Box, ListItem, ListItemIcon, ListItemText, styled } from "@mui/material";

const isWithinNewPeriod = (newUntilDate) => {
  if (!newUntilDate) return false;
  const now = new Date();
  const untilDate = new Date(newUntilDate);
  return now <= untilDate;
};

const StyledListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  padding: "8px 8px",
  height: 34,
  width: "100%",
  borderRadius: 10,
  color: active ? theme.palette.primary.text : theme.palette.neutral[600],
  background: active
    ? `linear-gradient(135deg, ${theme.palette.primary.surface}, rgba(1,187,167,0.08))`
    : "transparent",
  boxShadow: active
    ? `inset 2px 0 0 ${theme.palette.primary.main}`
    : "none",
  transition: "background-color 0.13s ease, color 0.13s ease",
  "& .MuiListItemIcon-root": {
    "& .MuiSvgIcon-root": {
      color: active ? theme.palette.primary.main : theme.palette.neutral[400],
    },
    "& svg": {
      "& path, & circle, & rect, & line, & polyline, & polygon": {
        stroke: active ? theme.palette.primary.main : theme.palette.neutral[400],
        transition: "stroke 0.12s ease, fill 0.12s ease",
      },
      '& [fill]:not([fill="none"])': {
        fill: active ? theme.palette.primary.main : theme.palette.neutral[400],
      },
    },
  },
  "&:hover": {
    backgroundColor: active ? undefined : theme.palette.neutral[50],
    color: active ? theme.palette.primary.text : theme.palette.neutral[800],
    "& .MuiListItemIcon-root": {
      "& .MuiSvgIcon-root": {
        color: active ? theme.palette.primary.main : theme.palette.neutral[600],
      },
      "& svg": {
        "& path, & circle, & rect, & line, & polyline, & polygon": {
          stroke: active ? theme.palette.primary.main : theme.palette.neutral[600],
        },
        '& [fill]:not([fill="none"])': {
          fill: active ? theme.palette.primary.main : theme.palette.neutral[600],
        },
      },
    },
  },
}));

const IconWrapper = styled(Box)({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const NotificationBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: -3,
  right: -3,
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: theme.palette.semantic.danger,
  border: `2px solid ${theme.palette.background.sidebar}`,
}));

const NewBadge = styled(Box)(({ theme }) => ({
  marginLeft: 6,
  padding: "1px 6px",
  borderRadius: 10,
  backgroundColor: theme.palette.semantic.danger,
  color: "#FFFFFF",
  fontSize: 9,
  fontWeight: 700,
  lineHeight: "14px",
  flexShrink: 0,
}));

const ComingSoonBadge = styled(Box)(({ theme }) => ({
  marginLeft: 6,
  padding: "1px 6px",
  borderRadius: 4,
  backgroundColor: theme.palette.semantic.warning,
  color: "#FFFFFF",
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: "0.04em",
  lineHeight: "14px",
  flexShrink: 0,
}));

const BetaBadge = styled(Box)(({ theme }) => ({
  marginLeft: 6,
  padding: "1px 6px",
  borderRadius: 4,
  backgroundColor: theme.palette.semantic.beta,
  color: "#FFFFFF",
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: "0.04em",
  lineHeight: "14px",
  flexShrink: 0,
}));

const StyledListItemIcon = styled(ListItemIcon)({
  minWidth: 22,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& .MuiSvgIcon-root": { fontSize: 16 },
  "& svg": { width: 14, height: 14 },
});

const StyledListItemText = styled(ListItemText, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ active }) => ({
  display: "flex",
  margin: 0,
  "& .MuiTypography-root": {
    fontSize: 12,
    fontWeight: active ? 600 : 500,
    lineHeight: 1.3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
}));

const SideNavItem = ({
  text,
  active,
  icon,
  hidden,
  // eslint-disable-next-line no-unused-vars
  isMiniNav,
  showBadge = false,
  newUntil,
  comingSoon = false,
  beta = false,
}) => {
  if (hidden) return null;

  const showNewBadge = isWithinNewPeriod(newUntil);

  return (
    <StyledListItem active={active}>
      <StyledListItemIcon>
        <IconWrapper>
          {icon}
          {showBadge && <NotificationBadge />}
        </IconWrapper>
      </StyledListItemIcon>
      <StyledListItemText active={active} primary={text} />
      {showNewBadge && !beta && <NewBadge>חדש</NewBadge>}
      {comingSoon && <ComingSoonBadge>בקרוב</ComingSoonBadge>}
      {beta && <BetaBadge>Beta</BetaBadge>}
    </StyledListItem>
  );
};

export default SideNavItem;
